// This is a JS file full of text. Text-in-JS.

/**
 * Format:
 * - tagName: explanText
 */
export const albumExplanations = {
  // ALBUMS
  "plein-air-acrylic": `
  Early in the summer of 2019, I bought a portable easel that I could carry on my bike, along with my other
  art supplies, and started doing acrylic landscape paintings of local landmarks. I quickly discovered that 
  by selecting a vantage point adjacent to a public thoroughfare on a nice day, 
  I could be treated to a pleasant stream of passers-by complimenting my work. Which turns out to be way more
  fun than painting from a photo, alone in my studio.`,

  "scenery-loc": `Unlike my Plein Air landscapes, which I painted on site, these are paintings I made from
  photographs. However, they were all photographs that I took, with the intention of painting them. That 
  sets them apart somewhat from pictures I copied from postcards or magazines, in that I at least viewed
  all of these scenes for myself before deciding they needed to be captured for posterity.`,

  "scenery-noloc": `As opposed to all my other landscape albums, these all came from photos I found
  somewhere, but probably never set foot near. For the most part, I don't even know exactly where 
  they came from; just someplace that looked nice.`,

  "gild": `Got a set of gilding materials for Christmas 2021, started adding metallic leaf to repertoire.`,

  "portrait-new": `After a long break from portraiture, I decided to give it another try this winter (2019),
  once it got too cold for my outdoor painting. Turns out not to be exactly like riding a bike, but I gradually
  got back into it. 'Luthien' is a friend who photographs well. The others are from a set of Watchmen-inspired
  artwork I made for my cousin's dorm room - it was the least I could do, after getting him hooked on my
  favorite comic book franchise.`,

  "logo": `Making logos is something I just do in my free time, whether or not a I need a logo for something.
  Mostly what I'm making is calligraphic monograms - artistic arrangements of initials. I've come up with a lot
  of them for the letters 'CIW', and also 'CK'.`,

  "sketch": `This was my first run at creating art on site, and requires a lot less packing than acrylic painting.
  I'm happy if I can make a landmark recognizable to someone who's seen it before.`,

  "monogram": `I've been kind of obsessed with monograms since long before I started thinking about art as
  something I could do. And they don't really require any kind of inspiration; all I need is some letters and
  a style of joining them together - preferably one I haven't used already, at least not in the past week. I've 
  come up with a few different styles for putting letters together, given limited space and the fact that nobody ever needs
  to be able to READ the letters, just recognize them. You can 1) form the letters in such a way that they
  don't overlap; 2) join them into a single shape; 3) stack the letters on top of each other; 4) weave the 
  letters together - like stacking but without consistent layering; or 5) do something interesting with the 
  intersection points, other than merely putting one on top of the other. I've got numerous examples of
  each method, with various initials (I used to use the initials AK, now I go by CK, and I've been playing with
  the letters 'CIW' since I realized this website needed a logo). Any letters besides those were probably for
  somebody else.`,

  "nametag": `I've been involved in several different groups that use name tags. A lot of them use single-use 
  stickers (the ones that say "Hello My Name Is" at the top), and that's why I taught myself calligraphy (pays 
  off every time I go to a Meetup). But some groups use permanent name tags, and I always make a habit of making
  mine really, really nice. Which inevitably leads to other participants asking if I can make them one, too.
  Which of course I can, and do. The only constraints are the size of the plastic name tag holders used by the
  specific group, and what information they require (eg just first name, or first, last, and preferred pronoun).
  I generally try to get their favorite color, too; just so the tag is more likely to go with their outfit.`,

  "illumination": `I started making Name Plates for people at work who had cubicles and therefore
  no placard on their office door. And then before I knew it, everybody wanted one. It's a person's name and
  whatever they're into - the place I worked when it started is a nonprofit for Aspergers, so a lot of people have
  really strong interests. In this gallery, the interest is in the description field, under the name.`,

  "knotwork": `The first thing I ever wanted to be able to draw was Celtic knotwork. I made several attempts as a
  child, but without a specific design to copy, my patterns always ended up looking more like intestines. So, when I 
  took up drawing as an adult, learning to copy knotwork designs was my first goal. Eventually I started making up my
  own designs that did not immediately remind one of digestion.`,

  "cards": `I've made a lot of cards. Sometimes I made a design and then mass-produced cards to sell for holidays;
  other times I make a specialized one-off for a particular person, and then think, Hey, I could print more of these!
  Either way, these were all sent to someone.`,

  "aane": `For most of the past decade, I've been affiliated with/employed by an organization called AANE. (What that
  stands for changed over time, but at least one of the A's is usually Aspergers.) Anyway, at one time, their logo
  was stunningly dull: just block letters, arranged in a square. I suggested there might be more interesting ways
  of displaying four letters. And then I proceded to demonstrate.`,

  "portrait-old": `One of my early artistic phases was sketching people from magazines. Sometimes I know who it is; 
  a lot of them were from ads. It's fun to draw someone recognizable, and have them be recognized. But it's
  also somehow more stressful; as though the subject might be offended if it doesn't look right. Irrational
  though it may be, it's sometimes more relaxing to draw someone nameless. (It's okay if they HAVE a name. I just
  don't need to know it in order to draw them.)`,

  "plaitwork": `The first artistic style that I really adopted as my own was Plaitwork, a variant of knotwork that ends up
  looking more like woven-reed placemats than anything else. This was back when I wasn't really comfortable even admitting
  that "artist" was a term I might like to one day apply to myself. But I was OK with being good at geometry, and at following
  simple rules. So, I came up with a detailed process that ultimately came down to very simple rules (which I'm going to 
  demonstrate rather than trying to describe).`,

  "archive": `My 'Miscellaneous' album, with everything that doesn't fit into some other category`,

  // FILTERS

  "pointillist": `When I started learning to paint with watercolors, I lacked access to quality watercolor paper. The 
  trouble with putting watercolor on paper that isn't designed for it is that the paper will start to curl as it becomes
  inconsistently damp. My solution was to apply the watercolor in tiny dots, so no part of the paper was ever wet enough
  to seriously change the shape or texture. Later, I found out about the French post-impressionist artist George Seurat and
  the Pointillist movement, though I suspect they got started for different reasons.`,

  "triptych": `A Triptych is a picture on 3 panels (from Greek Tri + Ptuchos, "panel"). Most of these came from my
  Sabbatical period, as a way of fitting neatly into a standardized envelope without permanently damaging the picture
  with creases. This way, the creases were in between the panels. Also, it makes it seem like you're looking at
  the world through bars of some kind.`,

  "nightscape": `During my first summer of Plein Air painting, I kept noticing that 1) I spent a lot of time out biking at
  night, when it was too dark to do my usual type of painting, and 2) the lights of Boston at night, especially looking
  the river, are spectacular. And then I discovered that both canvas and panel come in black.`,

  "miniature": `At some point, I got it into my head that making smaller paintings would mean less time commitment for 
  each piece. This turned out not to be the case. In fact, very small paintings took just as long as larger ones; I just
  had to use a smaller brush. I made a bunch of them, anyway. These are all either on 7" x 5" canvas, or 6" x 6" panels.`,

  "panorama": `Most of these pieces were created during my Sabbatical period, when all my work had to fit neatly into 
  a standard-sized envelope, because reasons. Since all my paper was 8.5" x 11", there were basically two ways to accomplish
  this: fold the paper in thirds and make a triptych, or just use an envelope-sized area of the paper. Hence, I have
  a lot of long thin pictures.`,

  "en plein air": `All the pieces that I created outdoors, 'in the fresh air'.`,

  "era-learning": ``,

  "early-work": ``,

  "sabbatical": `For a lot of my work to make sense, it's important to know that I spent some time with limited access to 
  high-qaulity artistic materials. The how and why of that experience is not particularly germaine; the relevant parts 
  are that during one of my most prolific periods, I had very limited access to materials, and also that everything I made
  on 8.5" x 11" sheets had to fit into a 9" x 4" envelope.`,

  "era-recent": `What I call my "recent" period spans the time between ending my sabbatical in July of 2015, and 
  taking an expected hiatus from art due to a disk error (L5-S1) in the fall of 2018.`,

  "era-current": `This period started in the spring of 2019, when my disk recovered enough to let me begin 
  painting again. Which I did mostly outdoors.`,

  "era-quarantine": `Art I created during the Covid-19 lockdown period, beginning in late March of 2020`,

  "era-melrose": `In grade school, I had a crush on a girl I knew named Susanna, but never really talked to her. Until
  2020, when I found her on a dating site, roughly 400 miles from where we both went to school. Long story short, now
  I live with her in Melrose, and she inspires a lot of my work.`,

  "for-sale": `These are paintings that I currently possess, and would be willing to part with for the amount listed in
  the field with the $ sign. Only, I don't currently have a convenient way to order them. So, if you're interested in
  acquiring an original piece, please just email me.`,

  "for-print": `These paintings I may or may not currently possess, but I've arranged to have them printed on various
  household products. Because hey, who doesn't love household products? You can also purchase prints, posters, and
  tote bags, emblazoned with my artwork.`,
  
  "nfs": `These are the pieces that aren't (currently) available for sale or print, but you're welcome to look at them to your 
  heart's content. Also, if you feel that you'd like to buy one of my pictures on a tote bag or something, but 
  the option is not currently available, please contact me and I'll make it happen.`,

  "downtown-boston": `It's a little tricky to say whether a lot of my paintings are in Boston or Cambridge, because
  quite often they depict a scene in one, while having been painted in the other. And I can never remember which way
  I'm doing it, so it's a little random. Luckily, I should have GPS coordinates for most of these. Just in case
  you don't recognize them.`
  
};


export const aboutContent = {
  "intro": [
    `My name is Charlie Kelly, and this is my website. It's mine in the sense that I own the domain,
    but also because I created all the artwork. And also did the engineering. But I had help on the design.
    All three topics are discussed below.`
  ],
  "art": [
    `I took up art essentially because I found out it was possible to take up art as an adult. 
    Which was mind-blowing to me – I’d always assumed stuff like art, music, sports, etc were something you 
    either got at birth or didn’t. But then I met a guy who started drawing in his 30’s, and he was pretty good! 
    Also, I had a lot of free time, in an environment where too much free time can get you in trouble.`,

    `So, I started making art. At first, I stuck to designs that were more mathematical than artistic. 
    I’ve always admired Celtic knotwork, but had always struggled with trying to create my own designs. 
    However, I came up with a style of my own that resembles plaitwork – like traditional knotwork in that it appears woven, 
    but sticking more rigidly to right angles, like a lattice. From there I branched out into more traditional knotwork, 
    and then into representational art. First plants and random objects, then people, and then landscapes and architecture. 
    I also evolved from graphite and colored pencil to watercolors to acrylics.`,

    `At present, I mostly do landscapes. When the weather is nice, I prefer to paint my landscapes 
    "en plein air", which means standing in front of the landscape while I'm painting it.`
  ],
  "design": [
    `I've been putting my artwork online in various ways for about as long as I've been drawing, but I've never
    been particularly happy with how it looked. So for this iteration, I decided to get help. Since I had just 
    completed a Web Development program at General Assembly, I asked some graduates of the UX Design program 
    to help me with that aspect.`,
    `The resulting collaboration took research from potential buyers to design a site, and then gathered user
    feedback to fine-tune the initial design.`
  ],
  "tech": [
    `I'm also into making websites. This is one such. In fact, if my goal were merely to sell art, 
    there are dozens of commercial platforms already available for that. What I really wanted to do
    is get really good at website (specifically ReactJS) while making a site that does EXACTLY what
    I want it to do, rather than merely the best I can do with the tools available. `,

    `When I said I made it from scratch, that's not technically accurate. I made it from
    create-react-app. The back end for this site (at the moment that I'm writing this) exists entirely
    within a free-tier account on Cloudinary. Everything other than retrieving images and metadata
    is done on the front end. `
  ] 
}

export const contactText = {
  "art": `If you're interested in my artwork, you can connect with me on these platforms
  to ask me questions, make comments, or subscribe to updates.`,
  "design": `These are my design consultants. If you like how the site looks, you should
  go hire them.`,
  "tech": `If you're interested in how this site is built, or want to build something with me,
  you can connect with me on one of these platforms.`
}